import React, {useContext, useEffect, useState} from "react";
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { CircularProgress } from "@mui/material";
import Button from "../components/button";
import Checkmark from "../checkmark.svg";
import CustomerContext from "../stores/customerContext";
import fetchProducts from "../services/fetchProducts";
import DonationTypes from "../stores/donationTypes";
import { formatCurrency } from "../utils/productHelpers";

export default function AddOns({type, displayName, Next, Previous, direction}) {
    const {customer, updateContextValue} = useContext(CustomerContext);
    const {amiType, leauxalType, rainAngelType} = DonationTypes();
    const [addOns, setAddOns] = useState([]);
    const [selectedAddOns, setSelectedAddOns] = useState(customer.selected_addons || []);
    const [isLoading, setIsLoading] = useState(true);

    // This is used for hover.  We show whichever add-on the donor is hovering
    // so they can preview its details before deciding to select it.
    const [hoveredAddOn, setHoveredAddOn] = useState(null);

    useEffect(() => {
        LoadAddOns();
    }, [type]);

    // Once we know whether this campaign has any add-ons, skip this step
    // entirely by continuing in whichever direction the donor was already
    // navigating (so "Back" from Contact Info doesn't bounce right back).
    useEffect(() => {
        if(!isLoading && addOns.length === 0){
            if(direction === "prev"){
                Previous();
            }
            else{
                Next();
            }
        }
    }, [isLoading, addOns]);

    async function LoadAddOns(){
        let data = await fetchProducts();
        let newAddOns = data.data.filter((prod) =>
            prod.metadata["Is-add-on"] === "True" && prod.metadata.donor_campaign === type
        );
        setAddOns(newAddOns);
        setIsLoading(false);
    }

    function IsSelected(addOn){
        return selectedAddOns.some((selected) => selected.id === addOn.id);
    }

    function ToggleAddOn(addOn){
        let newSelection;
        if(IsSelected(addOn)){
            newSelection = selectedAddOns.filter((selected) => selected.id !== addOn.id);
        }
        else{
            newSelection = [...selectedAddOns, addOn];
        }
        setSelectedAddOns(newSelection);
        updateContextValue({...customer, selected_addons: newSelection});
    }

    function ChooseClass(addOn){
        let className = '';
        switch(type){
            case amiType:
                className = `buttonAmi relative grow px-12 max-w-[18rem] my-[0.5rem] mr-[3rem] w-full py-6 text-center rounded-lg border-[#0082CA] border-[1px] hover:bg-[#00CFF5] hover:text-[#FAFAFA] ${IsSelected(addOn) ? 'font-bold text-[#FAFAFA] bg-[#0082CA]' : '' }`;
                break;
            case leauxalType:
                className = `buttonLeauxcal relative grow px-12 max-w-[18rem] my-[0.5rem] mr-[3rem] w-full py-6 text-center rounded-lg border-[#5F259F] border-[1px] hover:bg-[#CD00E1] hover:text-[#FAFAFA] ${IsSelected(addOn) ? 'font-bold text-[#FAFAFA] bg-[#5F259F]' : '' }`;
                break;
            case rainAngelType:
                className = `buttonRainAngel relative grow px-12 max-w-[18rem] my-[0.5rem] mr-[3rem] w-full py-6 text-center rounded-lg border-black border-[1px] hover:bg-[#4F4D52] hover:text-[#FAFAFA] ${IsSelected(addOn) ? 'font-bold text-[#FAFAFA] bg-black text-white' : '' }`;
                break;
            default:
                className = `relative grow px-12 max-w-[18rem] my-[0.5rem] mr-[3rem] w-full py-6 text-center rounded-lg border-black border-[1px] hover:bg-[#4F4D52] hover:text-[#FAFAFA] ${IsSelected(addOn) ? 'font-bold text-[#FAFAFA] bg-black' : '' }`;
                break;
        }
        return className;
    }

    if(isLoading || addOns.length === 0){
        return (
            <Grid container className="place-content-center w-full h-screen">
                <CircularProgress></CircularProgress>
            </Grid>
        );
    }

    return (
        <Box sx={{flexGrow: 1}}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <div className="text-[1.5rem] font-bold m-[2rem] px-[4rem]">
                        Would You Like To Add Any Extras?
                    </div>
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={6}>
                    {
                        addOns.map((addOn) =>
                            <button
                                key={addOn.id}
                                onClick={() => ToggleAddOn(addOn)}
                                onMouseOver={() => setHoveredAddOn(addOn)}
                                onMouseLeave={() => setHoveredAddOn(null)}
                                className={ChooseClass(addOn)}
                            >
                                {
                                    IsSelected(addOn) &&
                                    <span className="absolute top-2 right-2 bg-white rounded-full w-5 h-5 flex items-center justify-center">
                                        <img src={Checkmark} alt="Selected" />
                                    </span>
                                }
                                <div>
                                    <div>{formatCurrency(addOn.defaultPrice?.unitAmount)}</div>
                                    <div>{addOn.name}</div>
                                </div>
                            </button>
                        )
                    }
                </Grid>
                <Grid item xs={6}>
                    <h3 className="font-bold mt-[2rem] ml-6">
                        { hoveredAddOn ? hoveredAddOn.name : "Add-On Details" }
                    </h3>
                    <div className="p-6 m-6 border-2 border-black rounded-lg min-h-[8rem]">
                        {
                            hoveredAddOn ?
                            <>
                                {
                                    hoveredAddOn.description &&
                                    <div className="mb-4">{hoveredAddOn.description}</div>
                                }
                                {
                                    (hoveredAddOn.features || []).map((feature, index) =>
                                        <div key={index} className="ml-6 pt-2">
                                            <span>{feature.name}</span>
                                        </div>
                                    )
                                }
                            </>
                            :
                            <div className="text-gray-500">Hover over an add-on to see more details.</div>
                        }
                    </div>
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={6}>
                    <Button text={"Back"} displayName={displayName} onClick={Previous} />
                </Grid>
                <Grid item xs={6}>
                    <Button text={"Continue"} displayName={displayName} onClick={Next} />
                </Grid>
            </Grid>
        </Box>
    );
}
